// hotel/F&B/HotelAdminFBMgmt.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Select,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Avatar,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Fade,
  Zoom,
  Slide,
  Grow,
  Skeleton,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Delete,
  Visibility,
  Search,
  Refresh,
  Restaurant,
  Receipt,
  TrendingUp,
  Schedule,
  CheckCircle,
  DeliveryDining,
  TakeoutDining,
  RoomService,
  AttachMoney,
  Fastfood,
  LocalBar,
  Category,
  Print,
  Cancel,
  LocalHotel,
  Inventory,
  Warning,
  DoneAll,
  HourglassEmpty,
  EmojiEmotions,
  Phone,
  Person,
  TableRestaurant,
  Check,
  CleaningServices,
  EventSeat,
  WarningAmber,
  History,
  Today,
  People,
  Storefront
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';

import printingService from '../../Hotel/Hotel-Staff/F&B/printingService';

// Animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const OrderCard = styled(Card)(({ theme, status }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return theme.palette.warning.light;
      case 'confirmed': return theme.palette.info.light;
      case 'preparing': return theme.palette.primary.light;
      case 'ready': return theme.palette.success.light;
      case 'served': return theme.palette.success.main;
      case 'completed': return theme.palette.grey[300];
      case 'cancelled': return theme.palette.error.light;
      default: return theme.palette.grey[300];
    }
  };

  return {
    border: `2px solid ${getStatusColor(status)}`,
    borderRadius: '16px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    overflow: 'hidden',
    animation: `${fadeInUp} 0.5s ease-out`,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
    },
  };
});

const StatusBadge = styled(Box)(({ theme, status }) => {
  const getColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return theme.palette.warning.main;
      case 'confirmed': return theme.palette.info.main;
      case 'preparing': return theme.palette.primary.main;
      case 'ready': return theme.palette.success.main;
      case 'served': return theme.palette.success.dark;
      case 'completed': return theme.palette.grey[600];
      case 'cancelled': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  return {
    backgroundColor: getColor(status),
    color: 'white',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    animation: `${pulse} 1s infinite`,
  };
});

const InventoryItemCard = styled(Card)(({ theme, selected, lowstock }) => ({
  border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: lowstock ? theme.palette.error.lighter : theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
  '&::before': lowstock ? {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    backgroundColor: theme.palette.error.main,
  } : {},
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '20px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[12],
  },
}));

// Constants
const ORDER_TYPES = [
  { value: 'dine_in', label: 'Bar/Skyline', icon: <LocalBar />, color: '#1976D2', description: 'Dine at our bar or skyline lounge' },
  { value: 'Lounge', label: 'Lounge/Arena', icon: <LocalBar />, color: '#1976D2', description: 'Relax in our premium lounge area' },
  { value: 'room_service', label: 'Room Service', icon: <RoomService />, color: '#2E7D32', description: 'Delivered to your hotel room' },
  { value: 'takeaway', label: 'Takeaway', icon: <TakeoutDining />, color: '#ED6C02', description: 'Pick up your order to go' },
  { value: 'delivery', label: 'Delivery', icon: <DeliveryDining />, color: '#0288D1', description: 'Delivered to your address' },
];

const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending', icon: <HourglassEmpty />, color: '#FFC107' },
  { value: 'confirmed', label: 'Confirmed', icon: <CheckCircle />, color: '#1976D2' },
  { value: 'preparing', label: 'Preparing', icon: <Fastfood />, color: '#7B1FA2' },
  { value: 'ready', label: 'Ready', icon: <DoneAll />, color: '#2E7D32' },
  { value: 'served', label: 'Served', icon: <CheckCircle />, color: '#558B2F' },
  { value: 'completed', label: 'Completed', icon: <CheckCircle />, color: '#757575' },
  { value: 'cancelled', label: 'Cancelled', icon: <Cancel />, color: '#D32F2F' },
];

// API URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'Frankville Hotels';
const TAX_RATE = parseFloat(process.env.REACT_APP_HOTEL_TAX) || 0;
const SERVICE_CHARGE_RATE = parseFloat(process.env.REACT_APP_HOTEL_SERVICE_CHARGE) || 0;

// Helper Functions
const getCurrentStaffId = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.staff_id || storedProfile.id || storedProfile.staffId || null;
  } catch (error) {
    console.error('Error getting staff ID:', error);
    return null;
  }
};

const getCurrentStaffName = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.first_name || storedProfile.name || 'Admin';
  } catch (error) {
    console.error('Error getting staff name:', error);
    return 'Admin';
  }
};

const getHotelCode = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
  } catch (error) {
    console.error('Error getting hotel code:', error);
    return '';
  }
};

// Helper to get date string in YYYY-MM-DD format
const getDateOnly = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  return dateTimeStr.split(' ')[0].split('T')[0];
};

// API Service
const apiService = {
  getInventoryCategories: async () => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/inventory_categories.php`, {
      params: { hotel_code: getHotelCode() }
    });
    return response.data;
  },

  getInventoryItems: async (category = '', includeOutOfStock = false, search = '') => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/inventory.php`, {
      params: {
        hotel_code: getHotelCode(),
        category,
        include_out_of_stock: includeOutOfStock,
        search
      }
    });
    return response.data;
  },

  getTables: async (section = '') => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/f_b_tables.php`, {
      params: {
        hotel_code: getHotelCode(),
        section: section
      }
    });
    return response.data;
  },

  getRooms: async () => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/rooms.php`, {
      params: { hotel_code: getHotelCode() }
    });
    return response.data;
  },

  // Admin gets ALL orders (no staff_id filter)
  getOrders: async (params = {}) => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/orders.php`, {
      params: { 
        hotel_code: getHotelCode(),
        ...params 
      }
    });
    return response.data;
  },

  // Admin can cancel orders
  updateOrderStatus: async (orderId, status, cancellationReason = null) => {
    const requestData = {
      id: orderId,
      hotel_code: getHotelCode(),
      status,
      cancelled_by: getCurrentStaffId(),
      cancelled_by_name: getCurrentStaffName()
    };
    if (cancellationReason) {
      requestData.cancellation_reason = cancellationReason;
    }
    const response = await axios.put(`${API_BASE_URL}/hotel/F&B/orders.php`, requestData);
    return response.data;
  },

  getStatistics: async (period = 'today') => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/orders.php`, {
      params: {
        action: 'admin_statistics',
        hotel_code: getHotelCode()
      }
    });
    return response.data;
  },

  getStaffPerformance: async () => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/orders.php`, {
      params: {
        action: 'staff_performance',
        hotel_code: getHotelCode()
      }
    });
    return response.data;
  },

  getShiftStatus: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/F&B/shifts.php`, {
        params: {
          hotel_code: getHotelCode(),
          include_history: 'true'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting shift status:', error);
      return { success: false, active_shift: null, is_day_closed: false };
    }
  }
};

const HotelAdminFBMgmt = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // State management
  const [orders, setOrders] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredInventoryItems, setFilteredInventoryItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [staffPerformance, setStaffPerformance] = useState(null);

  // Pagination
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);

  // Dialog states
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openOrderDetailsDialog, setOpenOrderDetailsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Day Closure & Shift State
  const [isDayClosed, setIsDayClosed] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Inventory filter state - MOVED BEFORE useEffect that uses it
  const [selectedCategory, setSelectedCategory] = useState('');
  const [includeOutOfStock, setIncludeOutOfStock] = useState(false);
  const [itemSearch, setItemSearch] = useState('');

  // Filter orders
  const getFilteredOrders = useCallback(() => {
    let filtered = [...orders];
    
    // Filter by current shift if not showing history
    if (!showHistory && currentShift) {
      filtered = filtered.filter(order => order.shift_id === currentShift.id);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order =>
        order.order_number?.toLowerCase().includes(query) ||
        order.table_number?.toLowerCase().includes(query) ||
        (order.room_number?.toString().toLowerCase().includes(query)) ||
        order.guest_name?.toLowerCase().includes(query) ||
        order.created_by_name?.toLowerCase().includes(query) // Search by waiter name
      );
    }
    
    return filtered;
  }, [orders, showHistory, currentShift, searchQuery]);

  const filteredOrders = getFilteredOrders();

  const currentOrders = filteredOrders.slice(
    ordersPage * ordersRowsPerPage,
    ordersPage * ordersRowsPerPage + ordersRowsPerPage
  );

  // Filter inventory items function
  const filterInventoryItems = useCallback(() => {
    let filtered = [...inventoryItems];
    if (selectedCategory) {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    if (!includeOutOfStock) {
      filtered = filtered.filter(item => item.current_stock > 0);
    }
    if (itemSearch) {
      const searchLower = itemSearch.toLowerCase();
      filtered = filtered.filter(item =>
        item.item_name.toLowerCase().includes(searchLower) ||
        item.category.toLowerCase().includes(searchLower)
      );
    }
    setFilteredInventoryItems(filtered);
  }, [inventoryItems, selectedCategory, includeOutOfStock, itemSearch]);

  // Filter inventory items - useEffect AFTER state declaration
  useEffect(() => {
    filterInventoryItems();
  }, [filterInventoryItems]);

  // Load data functions
  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadOrders(),
        loadCategories(),
        loadInventoryItems(),
        loadRooms(),
        loadStatistics(),
        loadStaffPerformance(),
        loadShiftStatus()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Failed to load data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const data = await apiService.getOrders();
      if (data.success) {
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await apiService.getInventoryCategories();
      if (data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadInventoryItems = async () => {
    setInventoryLoading(true);
    try {
      const data = await apiService.getInventoryItems();
      if (data.success) {
        setInventoryItems(data.inventory || []);
        setFilteredInventoryItems(data.inventory || []);
      }
    } catch (error) {
      console.error('Error loading inventory items:', error);
    } finally {
      setInventoryLoading(false);
    }
  };

  const loadRooms = async () => {
    try {
      const data = await apiService.getRooms();
      if (data.success) {
        setRooms(data.rooms || []);
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await apiService.getStatistics();
      if (data.success) {
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const loadStaffPerformance = async () => {
    try {
      const data = await apiService.getStaffPerformance();
      if (data.success) {
        setStaffPerformance(data.staff_performance);
      }
    } catch (error) {
      console.error('Error loading staff performance:', error);
    }
  };

  const loadShiftStatus = async () => {
    try {
      const data = await apiService.getShiftStatus();
      if (data.success) {
        setIsDayClosed(data.is_day_closed || false);
        setCurrentShift(data.active_shift);
      }
    } catch (error) {
      console.error('Error loading shift status:', error);
    }
  };

  // Show snackbar function
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Main data load effect
  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadOrders();
      loadStatistics();
      loadStaffPerformance();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setOrdersPage(0);
  }, [showHistory, searchQuery]);

  // Admin order status update (cancellation only)
  const handleUpdateOrderStatus = async (orderId, newStatus, cancellationReason = null) => {
    try {
      const result = await apiService.updateOrderStatus(orderId, newStatus, cancellationReason);
      if (result.success) {
        showSnackbar(`Order ${newStatus.replace('_', ' ')} successfully!`, 'success');
        await loadOrders();
        await loadStatistics();
        await loadStaffPerformance();
        
        if (newStatus === 'cancelled') {
          await loadInventoryItems();
          handleCloseCancelDialog();
        }
      } else {
        showSnackbar(result.error || 'Failed to update order status', 'error');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      showSnackbar('Failed to update order status: ' + error.message, 'error');
    }
  };

  const handleOpenCancelDialog = (order) => {
    setOrderToCancel(order);
    setCancellationReason('');
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
    setOrderToCancel(null);
    setCancellationReason('');
  };

  const handleCancelOrder = async () => {
    if (!cancellationReason.trim()) {
      showSnackbar('Please provide a cancellation reason', 'error');
      return;
    }
    if (!orderToCancel) {
      showSnackbar('No order selected for cancellation', 'error');
      return;
    }
    await handleUpdateOrderStatus(orderToCancel.id, 'cancelled', cancellationReason);
  };

  const handleRefresh = () => {
    loadData();
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Print order function
  const printOrder = (order) => {
    try {
      const printWindow = window.open('', '_blank', 'width=400,height=600');
      if (!printWindow) {
        alert('Please allow popups for this site to print receipts.');
        return;
      }
      
      const orderType = ORDER_TYPES.find(ot => ot.value === order.order_type);
      const roomDisplay = order.room_number ? `Room ${order.room_number}${order.room_name ? ` - ${order.room_name}` : ''}` : '';
      const hotelName = HOTEL_NAME || 'Frankville Hotels';
      const staffName = order.created_by_name || 'Staff';
      const currentDate = new Date().toLocaleString();
      const orderDate = new Date(order.created_at).toLocaleString();

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Order Receipt - ${order.order_number}</title>
          <style>
            @media print {
              @page { 
                margin: 0; 
                size: 80mm auto; 
              }
              body { 
                font-family: 'Courier New', monospace;
                font-size: 13px;
                font-weight: bold;
                margin: 0;
                padding: 8px;
                width: 80mm;
              }
              * {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            * { 
              margin: 0; 
              padding: 0; 
              box-sizing: border-box; 
            }
            body {
              font-family: 'Courier New', monospace;
              font-size: 13px;
              font-weight: bold;
              padding: 8px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .header { 
              text-align: center; 
              margin-bottom: 12px; 
            }
            .header strong {
              font-weight: 900;
              font-size: 16px;
            }
            .title { 
              font-size: 18px; 
              font-weight: 900; 
              margin: 5px 0; 
            }
            .divider { 
              border-top: 2px dashed #000; 
              margin: 8px 0; 
            }
            .divider-thick { 
              border-top: 3px solid #000; 
              margin: 8px 0; 
            }
            .item-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 4px 0;
              font-weight: bold;
              font-size: 13px;
            }
            .footer { 
              text-align: center; 
              margin-top: 12px; 
              font-size: 12px;
              font-weight: bold;
            }
            .order-detail {
              font-weight: bold;
              font-size: 13px;
              margin: 3px 0;
            }
            .order-detail strong {
              font-weight: 900;
            }
            .item-header {
              display: flex;
              justify-content: space-between;
              font-weight: 900;
              font-size: 14px;
              border-bottom: 2px solid #000;
              padding-bottom: 5px;
              margin-bottom: 5px;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              font-weight: 900;
              font-size: 15px;
              margin: 4px 0;
            }
            .grand-total {
              display: flex;
              justify-content: space-between;
              font-weight: 900;
              font-size: 18px;
              margin: 8px 0;
              border-top: 3px solid #000;
              padding-top: 8px;
            }
            .thank-you {
              text-align: center;
              font-size: 16px;
              font-weight: 900;
              margin: 12px 0;
            }
            .timestamp {
              font-weight: bold;
              font-size: 12px;
            }
            .status-badge {
              text-align: center;
              font-weight: 900;
              font-size: 14px;
              padding: 5px;
              background: #f0f0f0;
              margin: 5px 0;
            }
            .staff-info {
              font-weight: bold;
              font-size: 12px;
              margin-top: 5px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div><strong style="font-size:18px;">${hotelName}</strong></div>
            <div style="font-weight:bold; font-size:14px;">Food & Beverage Service</div>
            <div class="divider"></div>
          </div>
          
          <div>
            <div class="order-detail"><strong>Order #</strong> ${order.order_number}</div>
            <div class="order-detail"><strong>Type:</strong> ${orderType?.label || order.order_type}</div>
            ${order.table_number ? `<div class="order-detail"><strong>Table:</strong> ${order.table_number}</div>` : ''}
            ${roomDisplay ? `<div class="order-detail"><strong>${roomDisplay}</strong></div>` : ''}
            ${order.guest_name && order.guest_name !== 'Walk-in Customer' ? `<div class="order-detail"><strong>Guest:</strong> ${order.guest_name}</div>` : ''}
            <div class="order-detail"><strong>Date:</strong> ${orderDate}</div>
            <div class="status-badge"><strong>Status:</strong> ${order.status.toUpperCase()}</div>
            <div class="staff-info"><strong>Waiter:</strong> ${staffName}</div>
          </div>
          
          <div class="divider"></div>
          
          <div>
            <div class="item-header">
              <span>QTY</span>
              <span>ITEM</span>
              <span>TOTAL</span>
            </div>
            ${order.items?.map(item => `
              <div class="item-row">
                <span style="font-weight:900;">${item.quantity}x</span>
                <span style="font-weight:bold;">${item.item_name.substring(0, 25)}</span>
                <span style="font-weight:900;">₦${(item.quantity * item.unit_price).toFixed(2)}</span>
              </div>
            `).join('')}
          </div>
          
          <div class="divider-thick"></div>
          
          <div>
            <div class="total-row">
              <span>Subtotal:</span>
              <span style="font-weight:900;">₦${parseFloat(order.total_amount || 0).toFixed(2)}</span>
            </div>
            ${TAX_RATE > 0 && parseFloat(order.tax_amount || 0) > 0 ? `
              <div class="total-row">
                <span>Tax (${TAX_RATE}%):</span>
                <span style="font-weight:900;">₦${parseFloat(order.tax_amount || 0).toFixed(2)}</span>
              </div>
            ` : ''}
            ${order.order_type === 'room_service' && parseFloat(order.service_charge || 0) > 0 ? `
              <div class="total-row">
                <span>Service Charge (${SERVICE_CHARGE_RATE}%):</span>
                <span style="font-weight:900;">₦${parseFloat(order.service_charge || 0).toFixed(2)}</span>
              </div>
            ` : ''}
          </div>
          
          <div class="grand-total">
            <span>TOTAL:</span>
            <span>₦${parseFloat(order.final_amount || 0).toFixed(2)}</span>
          </div>
          
          ${order.special_instructions ? `
            <div class="divider"></div>
            <div style="font-weight:bold; margin:5px 0;">
              <strong>📝 Special Instructions:</strong><br>
              ${order.special_instructions}
            </div>
          ` : ''}
          
          <div class="divider"></div>
          
          <div class="thank-you">Thank You! Please come again.</div>
          
          <div class="footer">
            <span class="timestamp">${currentDate}</span><br>
            <span class="staff-info">Printed by: Admin</span><br>
            ${hotelName} F&B System
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { 
                window.close(); 
              }, 800);
            }
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();
    } catch (error) {
      console.error('Error printing order:', error);
      alert('Failed to print receipt. Please try again.');
    }
  };

  // Render Orders Tab
  const renderOrdersTab = () => {
    const validOrders = filteredOrders.filter(o => o.status !== 'cancelled');
    const totalValidOrders = validOrders.length;
    const totalRevenue = validOrders.reduce((sum, order) => sum + (parseFloat(order.final_amount) || 0), 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayValidOrders = validOrders.filter(o => {
      const orderDate = new Date(o.created_at);
      orderDate.setHours(0, 0, 0, 0);
      return orderDate.getTime() === today.getTime();
    });
    const todayRevenue = todayValidOrders.reduce((sum, order) => sum + (parseFloat(order.final_amount) || 0), 0);
    const todayOrderCount = todayValidOrders.length;
    
    const pendingOrders = filteredOrders.filter(o => o.status === 'pending').length;
    
    const lowStockItems = inventoryItems.filter(item => 
      item.current_stock <= item.min_stock && item.is_active === 1
    ).length;

    return (
      <Box>
        {/* Admin Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }} icon={<Storefront />}>
          <Typography variant="body2">
            <strong>👑 Admin View:</strong> You are viewing all orders from all waiters. 
            You can cancel orders but cannot create new orders or modify order statuses.
          </Typography>
        </Alert>

        {/* DAY CLOSURE ALERT */}
        {isDayClosed && (
          <Alert severity="error" sx={{ mb: 3 }} icon={<WarningAmber />}>
            <Typography variant="body2">
              <strong>⚠️ DAY IS CLOSED!</strong> No new orders can be created by waiters.
            </Typography>
          </Alert>
        )}

        {/* Shift Info Card */}
        {currentShift && !isDayClosed && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#E8F5E9', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Today color="success" />
                <Typography variant="body2">
                  <strong>Active Shift:</strong> Started at {new Date(currentShift.resumed_at).toLocaleTimeString()}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={showHistory}
                    onChange={(e) => setShowHistory(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <History fontSize="small" />
                    <Typography variant="body2">Show Order History</Typography>
                  </Box>
                }
              />
            </Box>
          </Paper>
        )}

        {/* History Mode Indicator */}
        {showHistory && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<History />}>
            <Typography variant="body2">
              Showing all historical orders from previous shifts. 
              <Button size="small" onClick={() => setShowHistory(false)} sx={{ ml: 1 }}>
                Switch to Current Shift
              </Button>
            </Typography>
          </Alert>
        )}

        {/* Low Stock Alert */}
        {lowStockItems > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
            <Typography variant="body2">
              <strong>Low Stock Alert:</strong> {lowStockItems} item(s) are running low on stock. 
              Please restock soon to avoid running out.
            </Typography>
          </Alert>
        )}

        {/* Search and Action Buttons */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Search orders by number, table, room, guest name, or waiter name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={`Showing ${filteredOrders.length} orders`} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
            {!showHistory && currentShift && (
              <Chip 
                label="Current Shift Only" 
                size="small" 
                color="success" 
                icon={<Today />}
              />
            )}
            {showHistory && (
              <Chip 
                label="Showing History" 
                size="small" 
                color="info" 
                icon={<History />}
                onDelete={() => setShowHistory(false)}
              />
            )}
            {searchQuery && (
              <Chip 
                label={`Search: ${searchQuery}`} 
                size="small" 
                onDelete={() => setSearchQuery('')}
              />
            )}
            <Chip 
              label={`👤 ${new Set(orders.map(o => o.created_by_name)).size} Waiters`} 
              size="small" 
              color="secondary" 
              icon={<People />}
            />
          </Box>
        </Paper>

        {/* Orders List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {currentOrders.length === 0 ? (
                <Grid item xs={12}>
                  <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <Restaurant sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" color="textSecondary" gutterBottom>
                      No Orders Found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      {searchQuery ? 'Try adjusting your search criteria' : 
                       showHistory ? 'No historical orders found' :
                       'No orders have been placed by waiters yet'}
                    </Typography>
                  </Paper>
                </Grid>
              ) : (
                currentOrders.map((order, index) => (
                  <Grid item xs={12} md={6} lg={4} key={order.id}>
                    <Grow in timeout={index * 100}>
                      <OrderCard status={order.status}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                Order #{order.order_number}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {ORDER_TYPES.find(ot => ot.value === order.order_type)?.label}
                                {order.table_number && ` • Table ${order.table_number}`}
                                {order.room_number && ` • Room ${order.room_number}${order.room_name ? ` (${order.room_name})` : ''}`}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                <People fontSize="inherit" /> Waiter: {order.created_by_name || 'Unknown'}
                              </Typography>
                            </Box>
                            <StatusBadge status={order.status}>
                              {ORDER_STATUSES.find(s => s.value === order.status)?.icon}
                              {order.status.toUpperCase()}
                            </StatusBadge>
                          </Box>

                          <List dense>
                            {order.items && order.items.slice(0, 3).map((item, idx) => (
                              <ListItem key={idx} sx={{ px: 0 }}>
                                <ListItemText
                                  primary={item.item_name}
                                  secondary={`${item.quantity} x ₦${parseFloat(item.unit_price).toFixed(2)}`}
                                />
                                <Typography variant="body2" fontWeight="bold">
                                  ₦{(item.quantity * item.unit_price).toFixed(2)}
                                </Typography>
                              </ListItem>
                            ))}
                            {order.items && order.items.length > 3 && (
                              <ListItem sx={{ px: 0 }}>
                                <ListItemText primary={`+${order.items.length - 3} more items`} />
                              </ListItem>
                            )}
                          </List>

                          <Divider sx={{ my: 2 }} />

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="h6" color="primary">
                                ₦{parseFloat(order.final_amount).toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Created: {new Date(order.created_at).toLocaleTimeString()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Print Receipt">
                                <IconButton size="small" onClick={() => printOrder(order)}>
                                  <Print />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Details">
                                <IconButton size="small" onClick={() => {
                                  setSelectedOrder(order);
                                  setOpenOrderDetailsDialog(true);
                                }}>
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              {!['completed', 'cancelled'].includes(order.status) && (
                                <Tooltip title="Cancel Order">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleOpenCancelDialog(order)}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </OrderCard>
                    </Grow>
                  </Grid>
                ))
              )}
            </Grid>

            {filteredOrders.length > ordersRowsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(filteredOrders.length / ordersRowsPerPage)}
                  page={ordersPage + 1}
                  onChange={(event, page) => setOrdersPage(page - 1)}
                  color="primary"
                  size={isMobile ? 'small' : 'large'}
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  // Render Analytics Tab
  const renderAnalyticsTab = () => {
    const stats = statistics || {};
    const staffStats = staffPerformance || {};
    
    const totalOrders = stats.total_orders || 0;
    const totalRevenue = stats.total_revenue || 0;
    const averageOrderValue = stats.average_order_value || 0;
    
    // Get today's revenue from daily breakdown if available
    let todayRevenue = 0;
    let todayOrderCount = 0;
    if (stats.daily_breakdown && stats.daily_breakdown.length > 0) {
      const today = new Date().toISOString().split('T')[0];
      const todayData = stats.daily_breakdown.find(d => d.date === today);
      if (todayData) {
        todayRevenue = todayData.daily_revenue || 0;
        todayOrderCount = todayData.orders_count || 0;
      }
    }
    
    // Payment method breakdown from API
    const cashRevenue = stats.cash_revenue || 0;
    const posRevenue = stats.pos_revenue || 0;
    const transferRevenue = stats.transfer_revenue || 0;
    const qrRevenue = stats.qr_revenue || 0;
    const complimentaryCount = stats.complimentary_count || 0;
    const complimentaryAmount = stats.complimentary_amount || 0;

    const statCards = [
      { 
        title: 'Total Orders', 
        value: totalOrders, 
        icon: <Receipt />, 
        color: '#060929', 
        tooltip: 'Total orders (excludes cancelled)' 
      },
      { 
        title: 'Total Revenue', 
        value: `₦${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
        icon: <AttachMoney />, 
        color: '#4CAF50', 
        tooltip: 'From completed payments only' 
      },
      { 
        title: 'Avg Order Value', 
        value: `₦${averageOrderValue.toFixed(2)}`, 
        icon: <TrendingUp />, 
        color: '#2196F3', 
        tooltip: 'Average value per paid order' 
      },
      { 
        title: "Today's Revenue", 
        value: `₦${todayRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
        icon: <Schedule />, 
        color: '#1f0736', 
        tooltip: `${todayOrderCount} paid orders today` 
      },
      { 
        title: 'Cash Revenue', 
        value: `₦${cashRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
        icon: <AttachMoney />, 
        color: '#4CAF50', 
        tooltip: 'Revenue from cash payments' 
      },
      { 
        title: 'Card/POS Revenue', 
        value: `₦${posRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
        icon: <AttachMoney />, 
        color: '#2196F3', 
        tooltip: 'Revenue from POS terminal payments' 
      },
      { 
        title: 'Transfer Revenue', 
        value: `₦${transferRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
        icon: <AttachMoney />, 
        color: '#7B1FA2', 
        tooltip: 'Revenue from bank transfers' 
      },
      { 
        title: 'Complimentary', 
        value: complimentaryCount, 
        icon: <LocalBar />, 
        color: '#FF9800', 
        tooltip: `${complimentaryCount} complimentary orders` 
      },
    ];

    // Item sales
    const itemSales = orders.reduce((acc, order) => {
      if (order.status === 'cancelled') return acc;
      order.items?.forEach(item => {
        const itemName = item.item_name;
        if (!acc[itemName]) {
          acc[itemName] = { quantity: 0, revenue: 0 };
        }
        acc[itemName].quantity += item.quantity;
        acc[itemName].revenue += item.quantity * item.unit_price;
      });
      return acc;
    }, {});

    const popularItems = Object.entries(itemSales)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);

    const typeDistribution = ORDER_TYPES.map(type => {
      let matchingOrders;
      if (type.value === 'Lounge') {
        matchingOrders = orders.filter(o => o.order_type === 'Lounge' && o.status !== 'cancelled');
      } else {
        matchingOrders = orders.filter(o => o.order_type === type.value && o.status !== 'cancelled');
      }
      const count = matchingOrders.length;
      const revenue = matchingOrders.reduce((sum, o) => sum + (parseFloat(o.final_amount) || 0), 0);
      return { ...type, count, revenue };
    });

    const statusDistribution = ORDER_STATUSES.map(status => ({
      ...status,
      count: orders.filter(o => o.status === status.value).length
    }));

    // Staff performance data
    const staffList = staffStats.staff_list || [];
    const topWaiters = staffList
      .sort((a, b) => (b.total_orders || 0) - (a.total_orders || 0))
      .slice(0, 5);

    const lowStockCount = inventoryItems.filter(item => 
      item.current_stock <= item.min_stock && item.is_active === 1
    ).length;

    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{
          background: 'linear-gradient(135deg, #1a024b 0%, #140522 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          mb: 3
        }}>
          📊 F&B Analytics Dashboard
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>👑 Admin Analytics:</strong> This dashboard shows aggregated data from all waiters.
            Revenue calculations are based on <strong>ACTUAL PAYMENTS RECEIVED</strong>.
          </Typography>
        </Alert>

        {/* Payment Method Summary Card */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#E3F2FD', borderRadius: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            💳 Payment Method Breakdown
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">Cash</Typography>
                <Typography variant="h6" sx={{ color: '#4CAF50' }}>₦{cashRevenue.toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">POS Terminal</Typography>
                <Typography variant="h6" sx={{ color: '#2196F3' }}>₦{posRevenue.toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">Bank Transfer</Typography>
                <Typography variant="h6" sx={{ color: '#7B1FA2' }}>₦{transferRevenue.toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">QR Code</Typography>
                <Typography variant="h6" sx={{ color: '#FF9800' }}>₦{qrRevenue.toLocaleString()}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Zoom in timeout={index * 100}>
                <StatCard>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: stat.color }}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          {stat.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px' }}>
                          {stat.tooltip}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: stat.color, width: 45, height: 45 }}>
                        {stat.icon}
                      </Avatar>
                    </Box>
                  </CardContent>
                </StatCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Order Outlet Distribution */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Category /> Order Outlet Distribution
                </Typography>
                {totalOrders === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                    No valid orders to display
                  </Typography>
                ) : (
                  <List>
                    {typeDistribution.map((type) => {
                      const percentage = totalOrders > 0 ? (type.count / totalOrders * 100).toFixed(1) : 0;
                      const revenuePercentage = totalRevenue > 0 ? (type.revenue / totalRevenue * 100).toFixed(1) : 0;
                      return (
                        <ListItem key={type.value} divider>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: type.color, width: 40, height: 40 }}>
                              {type.icon}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={type.label}
                            secondary={`${type.count} orders (${percentage}%) • ₦${type.revenue.toLocaleString()} revenue (${revenuePercentage}%)`}
                          />
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" sx={{ color: type.color }}>
                              {type.count}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={parseFloat(percentage)}
                              sx={{ width: 80, borderRadius: 2, mt: 1 }}
                            />
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </StatCard>
          </Grid>

          {/* Order Status Overview */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule /> Order Status Overview
                </Typography>
                <List>
                  {statusDistribution.map((status) => {
                    const percentage = orders.length > 0 ? (status.count / orders.length * 100).toFixed(1) : 0;
                    const isCancelled = status.value === 'cancelled';
                    return (
                      <ListItem key={status.value} divider>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: status.color, width: 32, height: 32 }}>
                            {status.icon}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={status.label}
                          secondary={`${status.count} orders (${percentage}%)`}
                        />
                        <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: status.color }}>
                            {status.count}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={parseFloat(percentage)}
                            sx={{ 
                              width: 80, 
                              borderRadius: 2, 
                              mt: 1,
                              backgroundColor: isCancelled ? '#FFCDD2' : '#E0E0E0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: status.color
                              }
                            }}
                          />
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </StatCard>
          </Grid>

          {/* Top Performing Waiters */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People /> Top Performing Waiters
                </Typography>
                {topWaiters.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                    No waiter data available
                  </Typography>
                ) : (
                  <List>
                    {topWaiters.map((waiter, index) => (
                      <ListItem key={index} divider={index < topWaiters.length - 1}>
                        <ListItemIcon>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#17064d', minWidth: 35 }}>
                            #{index + 1}
                          </Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={waiter.staff_name || 'Unknown'}
                          secondary={`${waiter.total_orders || 0} orders • ₦${(waiter.total_revenue || 0).toLocaleString()} revenue`}
                        />
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {waiter.total_orders || 0} orders
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={topWaiters[0]?.total_orders > 0 ? ((waiter.total_orders || 0) / topWaiters[0].total_orders * 100) : 0}
                            sx={{ width: 80, borderRadius: 2, mt: 1 }}
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </StatCard>
          </Grid>

          {/* Most Popular Items */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Fastfood /> Most Popular Items
                </Typography>
                {popularItems.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                    No sales data available
                  </Typography>
                ) : (
                  <List>
                    {popularItems.map((item, index) => (
                      <ListItem key={index} divider={index < popularItems.length - 1}>
                        <ListItemIcon>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#17064d', minWidth: 35 }}>
                            #{index + 1}
                          </Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          secondary={`${item.quantity} sold • ₦${item.revenue.toLocaleString()} revenue`}
                        />
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {item.quantity} units
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(item.quantity / popularItems[0].quantity) * 100}
                            sx={{ width: 100, borderRadius: 2, mt: 1 }}
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </StatCard>
          </Grid>

          {/* Inventory Status */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Inventory /> Inventory Status
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Inventory sx={{ color: '#4CAF50' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Total Inventory Items" 
                      secondary={`${inventoryItems.length} active items`}
                    />
                    <Typography variant="h6">{inventoryItems.length}</Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning sx={{ color: lowStockCount > 0 ? '#FF9800' : '#4CAF50' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Low Stock Items" 
                      secondary={lowStockCount > 0 ? `${lowStockCount} items need restocking` : "All items well stocked"}
                    />
                    <Typography variant="h6" sx={{ color: lowStockCount > 0 ? '#FF9800' : '#4CAF50' }}>
                      {lowStockCount}
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: '#8BC34A' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="In Stock Items" 
                      secondary={`${inventoryItems.filter(i => i.current_stock > 0).length} items available`}
                    />
                    <Typography variant="h6">
                      {inventoryItems.filter(i => i.current_stock > 0).length}
                    </Typography>
                  </ListItem>
                </List>
              </CardContent>
            </StatCard>
          </Grid>

          {/* Revenue Summary */}
          <Grid item xs={12}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp /> Revenue Summary (Based on Actual Payments)
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" color="textSecondary">Total Revenue</Typography>
                      <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                        ₦{totalRevenue.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">From {totalOrders} paid orders</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" color="textSecondary">Today's Revenue</Typography>
                      <Typography variant="h4" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
                        ₦{todayRevenue.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">{todayOrderCount} paid orders today</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" color="textSecondary">Cash Revenue</Typography>
                      <Typography variant="h4" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
                        ₦{cashRevenue.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">Cash payments only</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" color="textSecondary">Non-Cash Revenue</Typography>
                      <Typography variant="h4" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                        ₦{(posRevenue + transferRevenue + qrRevenue).toLocaleString()}
                      </Typography>
                      <Typography variant="caption">POS + Transfer + QR</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const tabLabels = [
    { label: 'Orders', icon: <Receipt /> },
    { label: 'Analytics', icon: <TrendingUp /> },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderOrdersTab();
      case 1:
        return renderAnalyticsTab();
      default:
        return renderOrdersTab();
    }
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
          transition: 'margin 0.3s ease',
        }}
      >
        <TopNav
          onLogout={() => navigate('/HotelAdmin')}
          onNotificationClick={() => alert('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Paper elevation={0} sx={{
          p: 4,
          mt: 3,
          background: 'linear-gradient(135deg, #0e1da3 0%, #320e65 100%)',
          color: 'white',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🍽️ Food & Beverage Management
            </Typography>
            <Typography variant="subtitle1">
              <People sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              Admin view - Monitor all waiter orders
            </Typography>
          </Box>
          <Box sx={{
            position: 'absolute',
            right: -50,
            bottom: -50,
            opacity: 0.1,
            transform: 'rotate(-15deg)',
          }}>
            <Restaurant sx={{ fontSize: 200 }} />
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ mt: 3, borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '16px',
                minWidth: 120,
              },
              '& .Mui-selected': {
                color: '#120b41',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#0a0323',
                height: 3,
              },
            }}
          >
            {tabLabels.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <Badge
                    badgeContent={
                      index === 0 ? filteredOrders.filter(o => o.status !== 'cancelled').length : 0
                    }
                    color="primary"
                  >
                    {tab.label}
                  </Badge>
                }
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      </Box>

      {/* Cancel Order Dialog */}
      <Dialog open={openCancelDialog} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#09083b', color: 'white' }}>
          Cancel Order #{orderToCancel?.order_number}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
            Are you sure you want to cancel this order? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            <strong>Order Details:</strong><br />
            Waiter: {orderToCancel?.created_by_name || 'Unknown'}<br />
            Total: ₦{parseFloat(orderToCancel?.final_amount || 0).toFixed(2)}
          </Typography>
          <TextField
            fullWidth
            label="Cancellation Reason *"
            multiline
            rows={3}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="Please provide a reason for cancellation..."
            sx={{ mt: 2 }}
            required
            error={!cancellationReason.trim()}
            helperText={!cancellationReason.trim() ? 'Cancellation reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>Keep Order</Button>
          <Button onClick={handleCancelOrder} variant="contained" color="error" disabled={!cancellationReason.trim()}>
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={openOrderDetailsDialog} onClose={() => setOpenOrderDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Order #{selectedOrder?.order_number} Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>👤 Waiter:</strong> {selectedOrder.created_by_name || 'Unknown'}
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {ORDER_STATUSES.map((status) => (
                  <Chip
                    key={status.value}
                    label={status.label}
                    icon={status.icon}
                    color={selectedOrder.status === status.value ? 'primary' : 'default'}
                    variant={selectedOrder.status === status.value ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>

              {selectedOrder.status === 'cancelled' && selectedOrder.cancellation_reason && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Cancellation Reason:</Typography>
                  <Typography variant="body2">{selectedOrder.cancellation_reason}</Typography>
                </Alert>
              )}

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.item_name}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">₦{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                        <TableCell align="right">₦{(item.quantity * item.unit_price).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Subtotal:</Typography>
                  <Typography>₦{parseFloat(selectedOrder.total_amount || 0).toFixed(2)}</Typography>
                </Box>
                {TAX_RATE > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Tax ({TAX_RATE}%):</Typography>
                    <Typography>₦{parseFloat(selectedOrder.tax_amount || 0).toFixed(2)}</Typography>
                  </Box>
                )}
                {selectedOrder.order_type === 'room_service' && parseFloat(selectedOrder.service_charge || 0) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Service Charge ({SERVICE_CHARGE_RATE}%):</Typography>
                    <Typography>₦{parseFloat(selectedOrder.service_charge || 0).toFixed(2)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6" sx={{ color: '#1976D2' }}>
                    ₦{parseFloat(selectedOrder.final_amount || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {selectedOrder.special_instructions && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#FFF3E0', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Special Instructions:</Typography>
                  <Typography variant="body2">{selectedOrder.special_instructions}</Typography>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button variant="contained" startIcon={<Print />} onClick={() => printOrder(selectedOrder)} sx={{ bgcolor: '#1976D2' }}>
                  Print Receipt
                </Button>
                {!['completed', 'cancelled'].includes(selectedOrder.status) && (
                  <Button 
                    variant="contained" 
                    color="error" 
                    startIcon={<Cancel />}
                    onClick={() => {
                      setOpenOrderDetailsDialog(false);
                      handleOpenCancelDialog(selectedOrder);
                    }}
                  >
                    Cancel Order
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOrderDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelAdminFBMgmt;